import CT from '@marketplace-ai/tags';
import type { ColDef } from 'ag-grid-community';
import {
  AllCommunityModule,
  colorSchemeLightCold,
  ModuleRegistry,
  themeQuartz,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef } from 'react';
import { EmptyState, LoadingSpinner } from '~/components';
import { FilterBanner, useFilterBanner } from '~/components/agGrid/FilterBanner';
import {
  ACTIONS_COLUMN_BASE,
  ActionsRenderer,
  BASE_GRID_STYLE,
  type ActionDef,
} from '~/components/Table/gridUtil';
import { PROVIDER_PAGE_KEYS } from '~/routing/routes';
import { withNewRecordPill } from '../../newRecords/NewRecordPill';
import { useNewRecordPills } from '../../newRecords/useNewRecords';

import { cdsColumns } from './constants';
import { CdsLicenseFormValues } from './types';

ModuleRegistry.registerModules([AllCommunityModule]);
const theme = themeQuartz.withPart(colorSchemeLightCold).withParams(BASE_GRID_STYLE);

const CdsNoRowsOverlay = () => (
  <EmptyState
    pageKey={PROVIDER_PAGE_KEYS.licenses}
    title="No CDS licenses yet"
    cyTag={CT.account.licenses.cds.table.emptyState}
  />
);

interface Props {
  cdsLicenses: CdsLicenseFormValues[];
  loading: boolean;
  handleDeleteClick: (id: number) => void;
  handleEditClick: (id: number) => void;
}
export const CdsGrid = (props: Props) => {
  const { cdsLicenses, loading, handleDeleteClick, handleEditClick } = props;

  const colDefs = useMemo<ColDef[]>(
    () => [
      { field: 'id', hide: true },
      ...cdsColumns(),
      {
        ...ACTIONS_COLUMN_BASE,
        width: 80,
        cellRenderer: ActionsRenderer,
        cellRendererParams: {
          actions: [
            { type: 'edit', handler: handleEditClick, cyTag: CT.boardCerts.table.editBtn },
            { type: 'delete', handler: handleDeleteClick, cyTag: CT.boardCerts.table.deleteBtn },
          ] satisfies ActionDef[],
        },
      },
    ],
    [handleDeleteClick, handleEditClick]
  );

  const getRowId = useCallback(
    (params: { data: CdsLicenseFormValues }) => String(params.data.id),
    []
  );

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      cellStyle: { display: 'flex', alignItems: 'center' },
      filter: true,
      resizable: true,
      flex: 1,
      wrapText: true,
    };
  }, []);

  const gridRef = useRef<AgGridReact>(null);
  // [MCV-1812] Records a document parse just created carry a "New" pill for this visit.
  const newRecordIds = useNewRecordPills('cds_licenses', cdsLicenses);
  const columnDefs = useMemo(
    () => withNewRecordPill(colDefs, newRecordIds),
    [colDefs, newRecordIds]
  );
  const { filtersActive, handleFilterChanged, clearFilters, registerGridApi } = useFilterBanner();

  return (
    <div
      className="h-full w-full p-0 overflow-auto rounded-lg border border-grey-900 flex flex-col"
      data-cy={CT.boardCerts.table.container}
    >
      <FilterBanner active={filtersActive} onClear={clearFilters} />
      <div className="flex-1 min-h-0">
        <AgGridReact
          theme={theme}
          colResizeDefault="shift"
          enableFilterHandlers={true}
          rowData={cdsLicenses}
          loading={loading}
          loadingOverlayComponent={() => <LoadingSpinner size={12} />}
          noRowsOverlayComponent={CdsNoRowsOverlay}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={getRowId}
          pagination={true}
          paginationAutoPageSize={true}
          onFilterChanged={handleFilterChanged}
          onGridReady={(params) => registerGridApi(params.api)}
          ref={gridRef}
        />
      </div>
    </div>
  );
};
